<template>
  <div>
    <router-link to="/admin" class="text-capoeira-600 font-semibold mb-4 inline-block">
      ← Volver a Admin
    </router-link>

    <h1 class="text-4xl font-bold mb-8">📊 Gestor de Bracket</h1>

    <!-- Selector de categoría -->
    <div class="mb-8 bg-white p-6 rounded-lg shadow">
      <label class="block text-sm font-semibold mb-2 text-black">Seleccionar Categoría:</label>
      <select v-model="selectedCategoryId" class="form-select" @change="cargarBracket">
        <option value="">-- Selecciona una categoría --</option>
        <option
          v-for="cat in torneo.categorias"
          :key="cat.categoria_id"
          :value="cat.categoria_id"
        >
          {{ cat.nombre }}
        </option>
      </select>
    </div>

    <div v-if="!selectedCategoryId" class="text-center py-12 text-gray-500">
      Selecciona una categoría para continuar
    </div>

    <!-- Acciones -->
    <div v-else-if="loading" class="text-center py-12">
      <Loading message="Cargando bracket..." />
    </div>

    <div v-else class="space-y-8">
      <!-- Info de la categoría -->
      <div class="bg-blue-50 p-6 rounded-lg">
        <p class="text-lg text-black">
          <strong>Categoría:</strong> {{ selectedCategory.nombre }}
          | <strong>Competidores:</strong> {{ selectedCategory.total_competidores }}
        </p>
      </div>

      <!-- Botones de acción -->
      <div class="flex gap-4 flex-wrap">
        <button
          v-if="!selectedCategory.llaves_generadas"
          @click="generarLlaves"
          class="btn-primary"
        >
          Generar Llaves Iniciales
        </button>

        <button
          v-if="selectedCategory.llaves_generadas && puedeSiguienteRonda"
          @click="generarSiguienteRonda"
          class="btn-primary"
        >
          Generar Siguiente Ronda
        </button>

        <button
          v-if="selectedCategory.llaves_generadas"
          @click="regenerarLlaves"
          class="btn-danger"
        >
          🔄 Regenerar Llaves
        </button>

        <button
          @click="cargarBracket"
          class="btn-secondary"
        >
          🔄 Actualizar
        </button>
      </div>

      <!-- Bracket -->
      <div v-if="bracket" class="space-y-6">
        <div
          v-for="(ronda, idx) in bracket.rondas"
          :key="idx"
          class="bg-white p-6 rounded-lg shadow"
        >
          <h2 class="text-2xl font-bold mb-4">
            🎯 Ronda {{ ronda.numero_ronda }}
            <span class="text-sm text-gray-600">({{ ronda.total_combates }} combates)</span>
          </h2>

          <div class="space-y-4">
            <div
              v-for="llave in ronda.llaves"
              :key="llave.id"
              class="border rounded-lg p-4 hover:shadow-lg transition-shadow"
              :class="estadoClase(llave.estado)"
            >
              <!-- Combate -->
              <div v-if="!llave.es_bye" class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="font-bold text-lg text-yellow-600">
                    {{ llave.competidor1_apelido }}
                  </div>
                  <div class="text-gray-600 text-sm">
                    vs
                  </div>
                  <div class="font-bold text-lg text-yellow-600">
                    {{ llave.competidor2_apelido }}
                  </div>
                </div>

                <div class="ml-4 text-right">
                  <div class="text-sm text-gray-600 mb-2">
                    {{ llave.estado.toUpperCase() }}
                  </div>

                  <!-- Estado de rounds -->
                  <div class="flex gap-2">
                    <span
                      v-for="round in llave.rounds"
                      :key="round.numero_round"
                      :class="roundClass(round)"
                      class="text-xs font-bold py-1 px-2 rounded"
                    >
                      R{{ round.numero_round }}: {{ round.ganador_color || '-' }}
                    </span>
                  </div>

                  <!-- Ganador -->
                  <div v-if="llave.ganador_apelido" class="mt-2 text-green-600 font-bold text-lg">
                    ✓ {{ llave.ganador_apelido }}
                  </div>
                </div>
              </div>

              <!-- BYE -->
              <div v-else class="text-center">
                <div class="font-bold text-lg text-yellow-600">{{ llave.competidor1_apelido }}</div>
                <div class="text-yellow-600 font-semibold">📍 BYE (Avanza automáticamente)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Alert
        v-if="alert.show"
        :type="alert.type"
        :message="alert.message"
        :duration="3000"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import Loading from '../../components/Loading.vue'
import Alert from '../../components/Alert.vue'

const route = useRoute()
const api = useAPI()

const torneo = ref({})
const bracket = ref(null)
const selectedCategoryId = ref('')
const loading = ref(false)

const alert = ref({
  show: false,
  type: 'info',
  message: ''
})

const selectedCategory = computed(() => {
  return torneo.value.categorias?.find(
    c => c.categoria_id === parseInt(selectedCategoryId.value)
  ) || {}
})

const puedeSiguienteRonda = computed(() => {
  if (!bracket.value || !bracket.value.rondas.length) return false
  const ultimaRonda = bracket.value.rondas[bracket.value.rondas.length - 1]
  return ultimaRonda.llaves.every(l => l.estado === 'finalizado')
})

const estadoClase = (estado) => {
  const clases = {
    'pendiente': 'bg-yellow-50 border-yellow-200',
    'en_curso': 'bg-blue-50 border-blue-200',
    'finalizado': 'bg-green-50 border-green-200'
  }
  return clases[estado] || 'bg-gray-50 border-gray-200'
}

const roundClass = (round) => {
  const clases = {
    'pendiente': 'bg-gray-200 text-gray-800',
    'en_curso': 'bg-blue-200 text-blue-800',
    'finalizado': 'bg-green-200 text-green-800'
  }
  return clases[round.estado] || 'bg-gray-200'
}

const generarLlaves = async () => {
  try {
    loading.value = true
    await api.generarLlaves(route.params.id, selectedCategoryId.value)
    showAlert('success', 'Llaves generadas exitosamente')
    await cargarBracket()
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const generarSiguienteRonda = async () => {
  try {
    loading.value = true
    await api.siguienteRonda(route.params.id, selectedCategoryId.value)
    showAlert('success', 'Siguiente ronda generada')
    await cargarBracket()
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const regenerarLlaves = async () => {
  if (!confirm('¿Estás seguro? Esto eliminará todas las llaves, combates y votos de esta categoría.')) {
    return
  }

  try {
    loading.value = true
    await api.limpiarLlaves(route.params.id, selectedCategoryId.value)
    showAlert('success', 'Llaves eliminadas. Puedes generar nuevas.')
    await cargarTorneo()
    await cargarBracket()
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const cargarBracket = async () => {
  if (!selectedCategoryId.value) return

  try {
    loading.value = true
    bracket.value = await api.getBracket(route.params.id, selectedCategoryId.value)
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const showAlert = (type, message) => {
  alert.value = { show: true, type, message }
}

const cargarTorneo = async () => {
  try {
    torneo.value = await api.getTorneo(route.params.id)
  } catch (err) {
    showAlert('error', err.message)
  }
}

onMounted(async () => {
  await cargarTorneo()
})
</script>
